import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import { useStore } from "@xyflow/react";

const MODEL_OPTIONS = [
  { value: "gpt-4", label: "GPT-4" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  { value: "gpt-3.5-turbo-16k", label: "GPT-3.5 Turbo 16K" },
];

const RESPONSE_FORMAT_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "json", label: "JSON" },
  { value: "structured", label: "Structured" },
];

export const OpenAiModal = ({ open, node }) => {
  const nodeData = node?.data || {};
  const [settings, setSettings] = useState({
    // Model Configuration
    modelName: "gpt-4",

    // Simple useful settings
    nodeName: "",
    responseFormat: "text",
    systemMessage: "",

    // Legacy settings (for backward compatibility)
    selectedModel: "",
    prompt: "",
  });

  const initializedRef = useRef(false);
  const lastInitializedNodeId = useRef(null);

  // Initialize settings from node data when modal opens
  useEffect(() => {
    if (open && (!initializedRef.current || lastInitializedNodeId.current !== node?.id)) {
      const existingSettings = nodeData?.output?.settings || {};

      setSettings((prev) => ({
        ...prev,
        ...existingSettings,
        // Handle legacy format
        modelName: existingSettings.modelName || existingSettings.selectedModel || "gpt-4",
      }));

      initializedRef.current = true;
      lastInitializedNodeId.current = node?.id;
    }
  }, [open, node?.id, nodeData?.output?.settings]);

  // Update node data when settings change (debounced to prevent re-render loops)
  useEffect(() => {
    if (!node?.data?.setData || !open) return;

    const timer = setTimeout(() => {
      node.data.setData((oldData) => ({
        ...oldData,
        output: {
          ...oldData.output,
          settings,
        },
      }));
    }, 100);

    return () => clearTimeout(timer);
  }, [settings, node?.data?.setData, open]);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "auto",
        padding: 1,
      }}
    >
      {/* Model Configuration */}
      <Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Node Name */}
          <Box>
            <Typography
              sx={{
                color: "#888",
                fontSize: "12px",
                fontStyle: "italic",
                marginBottom: "6px",
                marginLeft: "0px",
              }}
            >
              Give this AI model a descriptive name
            </Typography>
            <TextField
              label="Node Name (Optional)"
              size="small"
              fullWidth
              value={settings.nodeName}
              onChange={(e) => handleSettingChange("nodeName", e.target.value)}
              sx={{
                backgroundColor: "#2E2E2E",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
              placeholder="e.g., Customer Support AI"
            />
          </Box>

          {/* Model Selection */}
          <Box>
            <Typography
              sx={{
                color: "#888",
                fontSize: "12px",
                fontStyle: "italic",
                marginBottom: "6px",
                marginLeft: "0px",
              }}
            >
              Choose which OpenAI model to use for processing
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={settings.modelName}
                onChange={(e) => handleSettingChange("modelName", e.target.value)}
                sx={{ backgroundColor: "#2E2E2E" }}
              >
                {MODEL_OPTIONS.map((model) => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Response Format */}
          <Box>
            <Typography
              sx={{
                color: "#888",
                fontSize: "12px",
                fontStyle: "italic",
                marginBottom: "6px",
                marginLeft: "0px",
              }}
            >
              Format for the AI's response output
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={settings.responseFormat}
                onChange={(e) => handleSettingChange("responseFormat", e.target.value)}
                sx={{ backgroundColor: "#2E2E2E" }}
              >
                {RESPONSE_FORMAT_OPTIONS.map((format) => (
                  <MenuItem key={format.value} value={format.value}>
                    {format.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* System Message */}
          <Box>
            <Typography
              sx={{
                color: "#888",
                fontSize: "12px",
                fontStyle: "italic",
                marginBottom: "6px",
                marginLeft: "0px",
              }}
            >
              Brief instruction for the AI's behavior
            </Typography>
            <TextField
              label="System Message (Optional)"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={settings.systemMessage}
              onChange={(e) => handleSettingChange("systemMessage", e.target.value)}
              sx={{
                backgroundColor: "#2E2E2E",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
              placeholder="You are a helpful assistant..."
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
